import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api from "@/lib/api";
import { Lock, CreditCard } from "lucide-react";

let _stripePromise = null;
const getStripePromise = () => {
  // Build ONCE and reuse a stable Promise; Stripe requires the <Elements stripe> value
  // to never change after first render (swapping it breaks CardElement/confirmCardSetup).
  if (!_stripePromise) {
    _stripePromise = api.get("/payments/config").then(({ data }) => loadStripe(data.publishable_key));
  }
  return _stripePromise;
};

const CARD_OPTS = {
  style: {
    base: { fontSize: "16px", color: "#0f172a", fontFamily: "inherit", "::placeholder": { color: "#94a3b8" } },
    invalid: { color: "#dc2626" },
  },
};

function InnerForm({ submitLabel, note, onConfirm }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setErr("The secure card form is still loading — please wait a moment and try again.");
      return;
    }
    setBusy(true); setErr("");
    try {
      let clientSecret;
      try {
        const { data } = await api.post("/payments/setup-intent");
        clientSecret = data.client_secret;
      } catch (siErr) {
        console.error("[BidMatchAI] setup-intent failed:", siErr?.response?.status, siErr?.response?.data || siErr?.message);
        setErr(siErr?.response?.data?.detail || "We couldn't start secure card setup. Please try again in a moment.");
        setBusy(false);
        return;
      }
      if (!clientSecret) {
        setErr("Card setup could not be initialized. Please refresh and try again.");
        setBusy(false);
        return;
      }
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        console.error("[BidMatchAI] confirmCardSetup error:", result.error);
        setErr(result.error.message || "Your card could not be verified. Please check the details or try another card.");
        setBusy(false);
        return;
      }
      await onConfirm(result.setupIntent.payment_method);
    } catch (e2) {
      console.error("[BidMatchAI] card form unexpected error:", e2?.response?.data || e2?.message || e2);
      setErr(e2?.response?.data?.detail || e2?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-4" data-testid="stripe-card-form">
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/30 transition-all">
        <CardElement options={CARD_OPTS} onReady={() => setReady(true)} />
      </div>
      {err && <p className="text-sm text-red-600" data-testid="card-error">{err}</p>}
      <button
        type="submit"
        disabled={!stripe || !ready || busy}
        data-testid="card-submit-button"
        className="w-full bg-emerald-900 text-white rounded-xl py-3.5 text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
      >
        {busy ? "Processing…" : <><CreditCard className="h-4 w-4" /> {submitLabel}</>}
      </button>
      <p className="text-xs text-slate-400 inline-flex items-center gap-1.5">
        <Lock className="h-3.5 w-3.5" /> {note || "Your card is encrypted and processed securely by Stripe. We never store card numbers."}
      </p>
    </form>
  );
}

export function StripeCardForm({ submitLabel, note, onConfirm }) {
  const [stripePromise] = useState(getStripePromise);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    stripePromise
      .then((s) => { if (mounted && !s) setLoadErr(true); })
      .catch((e) => { console.error("[BidMatchAI] Stripe.js failed to load:", e?.message || e); if (mounted) setLoadErr(true); });
    return () => { mounted = false; };
  }, [stripePromise]);

  if (loadErr) return <p className="text-sm text-red-600" data-testid="stripe-load-error">Could not load the secure card form. Please refresh.</p>;
  return (
    <Elements stripe={stripePromise}>
      <InnerForm submitLabel={submitLabel} note={note} onConfirm={onConfirm} />
    </Elements>
  );
}
